<template>
  <el-dialog v-model="visible" title="입고 처리" width="700px" @close="onClose">
    <!-- 자재 정보 요약 -->
    <div class="material-summary">
      <el-descriptions :column="3" size="small" border>
        <el-descriptions-item label="작업지시">{{ materialInfo.request_number }}</el-descriptions-item>
        <el-descriptions-item label="자재명">{{ materialInfo.item_name }}</el-descriptions-item>
        <el-descriptions-item label="규격/색상">
          {{ materialInfo.specification || '-' }} / {{ materialInfo.color || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="필요량">
          {{ Number(materialInfo.required_quantity).toLocaleString() }}
        </el-descriptions-item>
        <el-descriptions-item label="확보율">
          <el-progress
            :percentage="materialInfo.secured_percentage || 0"
            :stroke-width="14"
            :text-inside="true"
            style="width: 120px"
          />
        </el-descriptions-item>
        <el-descriptions-item label="공급사">{{ materialInfo.supplier_name || '-' }}</el-descriptions-item>
      </el-descriptions>
    </div>

    <!-- 발주항목 목록 -->
    <div v-loading="loadingItems" class="order-items-section">
      <div class="section-title">발주항목 목록</div>
      <el-table
        v-if="orderItems.length > 0"
        :data="orderItems"
        size="small"
        stripe
        highlight-current-row
        :row-class-name="rowClassName"
        @row-click="selectItem"
        class="order-items-table"
      >
        <el-table-column label="공급사" prop="supplier_name" width="100" />
        <el-table-column label="발주일" prop="order_date" width="100" />
        <el-table-column label="발주량" width="80" align="right">
          <template #default="{ row }">
            {{ Number(row.order_quantity).toLocaleString() }}
          </template>
        </el-table-column>
        <el-table-column label="입고누계" width="80" align="right">
          <template #default="{ row }">
            {{ Number(row.received_quantity).toLocaleString() }}
          </template>
        </el-table-column>
        <el-table-column label="잔여량" width="80" align="right">
          <template #default="{ row }">
            <span :class="{ 'text-danger': row.remaining_quantity > 0 }">
              {{ Number(row.remaining_quantity).toLocaleString() }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="상태" width="80" align="center">
          <template #default="{ row }">
            <el-tag
              :type="row.received_quantity >= row.order_quantity ? 'success' : 'warning'"
              size="small"
            >
              {{ row.received_quantity >= row.order_quantity ? '완료' : '미완료' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-else-if="!loadingItems" description="발주된 항목이 없습니다." :image-size="60" />
    </div>

    <!-- 입고 입력 폼 -->
    <div v-if="selectedItem" class="receiving-form-section">
      <div class="section-title">
        입고 입력
        <el-tag size="small" type="info" class="selected-tag">
          {{ selectedItem.supplier_name }} / 잔여 {{ Number(selectedItem.remaining_quantity).toLocaleString() }}
        </el-tag>
      </div>
      <el-form :model="form" label-width="90px" size="default">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="입고유형">
              <el-radio-group v-model="form.receiving_type">
                <el-radio value="COMPANY">회사입고</el-radio>
                <el-radio value="FACTORY_DIRECT">공장직송</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="입고창고">
              <el-select v-model="form.warehouse" clearable style="width: 100%"
                :placeholder="form.receiving_type === 'FACTORY_DIRECT' ? '공장 선택' : '창고 선택'"
              >
                <el-option
                  v-for="wh in filteredWarehouses"
                  :key="wh.id"
                  :label="`${wh.warehouse_type_display} - ${wh.name}`"
                  :value="wh.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="입고일">
              <el-date-picker v-model="form.received_date" type="date" style="width: 100%" value-format="YYYY-MM-DD" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="입고수량">
              <el-input-number v-model="form.quantity" :min="0" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="비고">
              <el-input v-model="form.remarks" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <template #footer>
      <el-button @click="visible = false">닫기</el-button>
      <el-button type="primary" :disabled="!selectedItem || form.quantity <= 0" :loading="submitting" @click="submit">
        입고 처리
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axiosInstance from '@/api/axios'
import { ElMessage } from 'element-plus'

const visible = ref(false)
const loadingItems = ref(false)
const submitting = ref(false)
const warehouses = ref([])
const orderItems = ref([])
const selectedItem = ref(null)
const materialInfo = ref({})

const form = ref({
  receiving_type: 'COMPANY',
  warehouse: null,
  received_date: new Date().toISOString().split('T')[0],
  quantity: 0,
  remarks: '',
})

const filteredWarehouses = computed(() => {
  if (form.value.receiving_type === 'FACTORY_DIRECT') {
    return warehouses.value.filter(wh => wh.warehouse_type === 'FACTORY')
  }
  return warehouses.value.filter(wh => wh.warehouse_type !== 'FACTORY')
})

const rowClassName = ({ row }) => {
  if (selectedItem.value && selectedItem.value.id === row.id) return 'selected-row'
  if (row.received_quantity >= row.order_quantity) return 'completed-row'
  return ''
}

const selectItem = (row) => {
  if (row.received_quantity >= row.order_quantity) {
    ElMessage.warning('이미 입고 완료된 항목입니다.')
    return
  }
  selectedItem.value = row
  form.value.quantity = row.remaining_quantity
}

const fetchWarehouses = async () => {
  try {
    const res = await axiosInstance.get('warehouses/', { params: { is_active: true } })
    warehouses.value = res.data.results || res.data
  } catch (e) {
    console.error('Failed to fetch warehouses:', e)
  }
}

const fetchOrderItems = async (materialId) => {
  loadingItems.value = true
  try {
    const res = await axiosInstance.get('/material-order-items/', {
      params: { main_request_material: materialId }
    })
    orderItems.value = res.data.results || res.data
    // 미완료 항목이 1개이면 자동 선택
    const pending = orderItems.value.filter(i => i.received_quantity < i.order_quantity)
    if (pending.length === 1) {
      selectItem(pending[0])
    }
  } catch (e) {
    console.error('발주항목 조회 실패:', e)
  } finally {
    loadingItems.value = false
  }
}

const open = (material) => {
  materialInfo.value = { ...material }
  selectedItem.value = null
  orderItems.value = []
  form.value = {
    receiving_type: 'COMPANY',
    warehouse: null,
    received_date: new Date().toISOString().split('T')[0],
    quantity: 0,
    remarks: '',
  }
  visible.value = true
  fetchOrderItems(material.id)
}

const emit = defineEmits(['submitted'])

const submit = async () => {
  if (!selectedItem.value) return
  submitting.value = true
  try {
    await axiosInstance.post('/receivings/', {
      material_order_item: selectedItem.value.id,
      receiving_type: form.value.receiving_type,
      warehouse: form.value.warehouse,
      received_date: form.value.received_date,
      quantity: form.value.quantity,
      remarks: form.value.remarks,
    })
    ElMessage.success('입고 처리 완료')
    // 목록 새로고침 (연속 입고 가능)
    await fetchOrderItems(materialInfo.value.id)
    selectedItem.value = null
    form.value.quantity = 0
    form.value.remarks = ''
    emit('submitted')
  } catch (e) {
    console.error('입고 처리 실패:', e)
    ElMessage.error('입고 처리 실패')
  } finally {
    submitting.value = false
  }
}

const onClose = () => {
  selectedItem.value = null
  orderItems.value = []
}

onMounted(() => {
  fetchWarehouses()
})

defineExpose({ open })
</script>

<style scoped>
.material-summary {
  margin-bottom: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-items-section {
  margin-bottom: 16px;
}

.order-items-table {
  cursor: pointer;
}

.receiving-form-section {
  background: #f5f7fa;
  padding: 16px;
  border-radius: 6px;
}

.selected-tag {
  font-weight: normal;
}

.text-danger {
  color: #F56C6C;
  font-weight: 600;
}

:deep(.selected-row) {
  background-color: #ecf5ff !important;
}

:deep(.completed-row) {
  color: #c0c4cc;
}
</style>
